/**
 * RolesAndPermissions
 *
 * Matrix view of modules x roles with R/W/D flags. In view mode it fetches the
 * latest permissions from the API; in edit mode it allows toggling checkboxes and
 * emits a flattened `updatedPermissions` array that the parent can persist. Write
 * implies Edit. Some cells are disabled to protect Super Admin and Role Management.
 */
import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./roles-and-permissions.css";
import { getAllRolesPermissions } from "../../../api/roles";
import { useLoading } from "../../../context/LoadingContext";

const getModuleNames = (modules) => {
  return modules.map((mod) => mod.moduleName);
};

const getUniqueRoles = (modules) => {
  const allRoles = modules.flatMap((mod) =>
    mod.roles.map((role) => role.roleName),
  );
  return Array.from(new Set(allRoles));
};

const sortRoles = (roles) => {
  const lPattern = /^L(\d+)$/i;
  return [...roles].sort((a, b) => {
    const ma = lPattern.exec(a);
    const mb = lPattern.exec(b);
    if (ma && mb) {
      return Number.parseInt(mb[1], 10) - Number.parseInt(ma[1], 10);
    }
    if (ma && !mb) return -1;
    if (!ma && mb) return 1;
    return b.localeCompare(a);
  });
};

const getRoleIdMapping = (modules) => {
  const mapping = {};
  for (const mod of modules) {
    for (const role of mod.roles) {
      if (!mapping[role.roleName]) {
        mapping[role.roleName] = role.roleId;
      }
    }
  }
  return mapping;
};

const getInitialPermissions = (modules, uniqueRoles) => {
  const initialPermissions = {};
  for (const mod of modules) {
    initialPermissions[mod.moduleName] = {};

    for (const role of mod.roles) {
      initialPermissions[mod.moduleName][role.roleName] = {
        ...role.permissions,
      };
    }

    for (const roleName of uniqueRoles) {
      if (!initialPermissions[mod.moduleName][roleName]) {
        initialPermissions[mod.moduleName][roleName] = {
          read: 0,
          write: 0,
          edit: 0,
          delete: 0,
        };
      }
    }
  }
  return initialPermissions;
};

const RolesAndPermissions = ({
  editMode,
  setUpdatedPermissions,
  setLastUpdatedAt,
  refreshKey,
}) => {
  const [apiData, setApiData] = useState(null);
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionsData, setPermissionsData] = useState({});
  const [roleIdMapping, setRoleIdMapping] = useState({});

  const permissions = ["R", "W", "D"];
  const { showLoading, hideLoading } = useLoading();

  const getDisplayRoleName = (roleName) => {
    return roleName === "Super Admin" ? "Admin" : roleName;
  };

  const fetchData = useCallback(async () => {
    showLoading();
    try {
      const response = await getAllRolesPermissions();
      const data = response.data;
      setApiData(data);

      if (data.lastModifiedAt) {
        setLastUpdatedAt(data.lastModifiedAt);
      }

      setModules(getModuleNames(data.modules));

      const uniqueRoles = getUniqueRoles(data.modules);
      setRoles(sortRoles(uniqueRoles));

      setRoleIdMapping(getRoleIdMapping(data.modules));
      setPermissionsData(getInitialPermissions(data.modules, uniqueRoles));
    } catch (error) {
      console.error("Failed to fetch all roles permissions.", error?.message);
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, setLastUpdatedAt]);

  useEffect(() => {
    fetchData();

    return () => {
      setApiData(null);
      setModules([]);
      setRoles([]);
      setPermissionsData({});
      setRoleIdMapping({});
    };
  }, [fetchData, refreshKey]);

  // Helper: Initialize role permissions if they don't exist
  const initializeRolePermissions = (
    newPermissionsData,
    moduleName,
    roleName,
  ) => {
    if (!newPermissionsData[moduleName]) {
      newPermissionsData[moduleName] = {};
    }
    newPermissionsData[moduleName][roleName] = {
      read: 0,
      write: 0,
      edit: 0,
      delete: 0,
    };
    return newPermissionsData[moduleName][roleName];
  };

  // Helper: Update permission value based on type
  const updatePermissionValue = (rolePermissions, permissionType, value) => {
    if (permissionType === "r") {
      rolePermissions.read = value;
    } else if (permissionType === "w") {
      rolePermissions.write = value;
      rolePermissions.edit = value; // W implies E
    } else if (permissionType === "d") {
      rolePermissions.delete = value;
    }
  };

  // Helper: Find module data by module name
  const findModuleData = (moduleName) => {
    return apiData.modules.find((m) => m.moduleName === moduleName);
  };

  // Helper: Find role data in module
  const findRoleData = (moduleData, roleName) => {
    if (!moduleData) {
      return null;
    }
    return moduleData.roles.find((r) => r.roleName === roleName);
  };

  // Helper: Get role ID from role data or fallback mapping
  const getRoleId = (roleData, roleIdFallback) => {
    if (roleData) {
      return roleData.roleId;
    }
    return roleIdFallback;
  };

  // Helper: Build permission entry for a role-module combination
  const buildPermissionEntry = (
    mod,
    role,
    newPermissionsData,
    moduleData,
    roleData,
    roleIdFallback,
  ) => {
    return {
      roleId: getRoleId(roleData, roleIdFallback),
      moduleId: moduleData.moduleId,
      status: true,
      read: newPermissionsData[mod][role].read === 1,
      write: newPermissionsData[mod][role].write === 1,
      edit: newPermissionsData[mod][role].edit === 1,
      canDelete: newPermissionsData[mod][role].delete === 1,
    };
  };

  // Helper: Build updated permissions list from nested loops
  const buildUpdatedPermissionsList = (newPermissionsData) => {
    const updatedList = [];
    for (const mod in newPermissionsData) {
      for (const role in newPermissionsData[mod]) {
        const moduleData = findModuleData(mod);
        const roleData = findRoleData(moduleData, role);
        const roleIdFallback = roleIdMapping[role];
        if (roleData || roleIdFallback) {
          const entry = buildPermissionEntry(
            mod,
            role,
            newPermissionsData,
            moduleData,
            roleData,
            roleIdFallback,
          );
          updatedList.push(entry);
        }
      }
    }
    return updatedList;
  };

  const handleCheckboxChange = (
    moduleName,
    roleName,
    permission,
    isChecked,
  ) => {
    const p = permission.toLowerCase();
    const newPermissionsData = structuredClone(permissionsData);
    let rolePermissions = newPermissionsData[moduleName]?.[roleName];

    if (!rolePermissions) {
      rolePermissions = initializeRolePermissions(
        newPermissionsData,
        moduleName,
        roleName,
      );
    }

    if (rolePermissions) {
      const value = isChecked ? 1 : 0;
      updatePermissionValue(rolePermissions, p, value);
      setPermissionsData(newPermissionsData);
      const updatedList = buildUpdatedPermissionsList(newPermissionsData);
      setUpdatedPermissions(updatedList);
    }
  };

  const getPermissionValue = (moduleName, roleName, permission) => {
    const p = permission.toLowerCase();
    const rolePermissions = permissionsData[moduleName]?.[roleName];
    if (!rolePermissions) return false;

    if (p === "r") return rolePermissions.read === 1;
    if (p === "w") return rolePermissions.write === 1;
    if (p === "d") return rolePermissions.delete === 1;

    return false;
  };

  // Helper: Check if module is permissions module
  const isPermissionsModule = (module) => {
    return module.trim().toLowerCase() === "permissions";
  };

  // Helper: Check if role is super admin
  const isSuperAdmin = (role) => {
    return role.toLowerCase() === "super admin";
  };

  // Helper: Check if checkbox should be disabled
  const shouldDisableCheckbox = (module, role) => {
    const isSuperAdminRole = isSuperAdmin(role);
    return !editMode || isSuperAdminRole;
  };

  // Helper: Get checkbox className
  const getCheckboxClassName = (module, role) => {
    const isSuperAdminRole = isSuperAdmin(role);
    const isEditMode = editMode && !isSuperAdminRole;
    return isEditMode ? "edit-mode" : "view-mode";
  };

  // Helper: Handle checkbox change
  const handleCheckboxChangeForCell = (module, role, permission, isChecked) => {
    handleCheckboxChange(module, role, permission, isChecked);
  };

  // Helper: Render permission cell
  const renderPermissionCell = (module, role, permission) => {
    const isPermsModule = isPermissionsModule(module);
    const isApprove = permission === "A";
    const isDisabled = shouldDisableCheckbox(module, role);
    const checkboxClassName = getCheckboxClassName(module, role);

    if (isPermsModule && isApprove) {
      return null;
    }

    // Accessible label text for screen readers
    const labelText = `${module} - ${role} - ${permission} permission`;
    return (
      <td key={`${module}-${role}-${permission}`}>
        <label
          className={`custom-checkbox ${checkboxClassName}`}
          htmlFor={`checkbox-${module}-${role}-${permission}`}
        >
          <input
            id={`checkbox-${module}-${role}-${permission}`}
            type="checkbox"
            disabled={isDisabled}
            checked={getPermissionValue(module, role, permission)}
            onChange={(e) =>
              handleCheckboxChangeForCell(
                module,
                role,
                permission,
                e.target.checked,
              )
            }
            aria-label={labelText}
          />
          <span className="checkmark"></span>
          <span
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
            }}
          >
            {labelText}
          </span>
        </label>
      </td>
    );
  };

  // Helper: Render permission cells for a role
  const renderPermissionCellsForRole = (module, role) => {
    return permissions.map((p) => renderPermissionCell(module, role, p));
  };

  return (
    <div className="roles-page">
      <div className="table-wrapper">
        <table className="permissions-table">
          <thead>
            <tr>
              <th className="sticky-col top-left" scope="col">
                Modules
              </th>
              {roles.map((role) => (
                <th
                  key={role}
                  colSpan={permissions.length}
                  scope="colgroup"
                  style={{ color: "#403C6F" }}
                >
                  {getDisplayRoleName(role)}
                </th>
              ))}
            </tr>

            <tr>
              <th className="sticky-col" scope="col">
                <span
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                    clip: "rect(0 0 0 0)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Modules
                </span>
              </th>
              {roles.flatMap((role) =>
                permissions.map((p) => (
                  <th key={`${role}-${p}`} scope="col">
                    {p}
                  </th>
                )),
              )}
            </tr>
          </thead>

          <tbody>
            {modules.map((module) => (
              <tr key={module}>
                <td className="sticky-col">{module}</td>
                {roles.flatMap((role) =>
                  renderPermissionCellsForRole(module, role),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

RolesAndPermissions.propTypes = {
  editMode: PropTypes.bool.isRequired,
  setUpdatedPermissions: PropTypes.func.isRequired,
  setLastUpdatedAt: PropTypes.func.isRequired,
  refreshKey: PropTypes.number,
};

export default RolesAndPermissions;
